<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'

import { computed, isRef } from 'vue'

import { useTabSessionStore } from '~/stores/tabSession'
import { useSidebarData } from '~/composables/useSidebarData'
import { useHouseholdPicker } from '~/composables/useHouseholdPicker'
import type { Role } from '~/types'

const toast = useToast()
void toast

onMounted(async () => {
  // Populate the "Switch user" menu with the accounts on this device (401s
  // harmlessly to an empty list when not signed in).
  loadAccounts()

  const cookie = useCookie('cookie-consent')
  if (cookie.value === 'accepted') {
    return
  }
  // Cookie-consent toast intentionally disabled for now — kept the
  // useCookie read so the future re-enable path is one block away.
})

const tabSession = useTabSessionStore()

// Header household picker (search + popover + select). Owns its own
// debounce, fetch wiring, and post-select navigation. The selector
// state we render here is read from the composable.
const {
  searchTerm,
  selectedHousehold,
  isSearching,
  groups,
  canViewSelector,
  minValue,
  maxValue,
  inceptionFrom,
  inceptionTo,
  pickerFilterActive,
  sortBy,
  sortOptions,
  canFilterByRep,
  repSearch,
  repId,
  selectedRepName,
  repItems,
  repIsSearching,
  pickRep,
  clearRep,
  onSelect,
  restoreFromSession,
  resetPickerFilters
} = useHouseholdPicker()

// Toggles the inline filter overlay inside the household-picker popover.
const showPickerFilters = ref(false)

// Mobile only: the household picker renders as a full-screen bottom sheet
// (USlideover) instead of the desktop popover — this drives its open state.
const mobilePickerOpen = ref(false)

const curHouseholdId = computed(() => (tabSession.householdId ?? selectedHousehold.value?.householdId) ?? 0)

// Sidebar rows for the active household. The composable watches the
// household id internally and re-fetches with cache-aware semantics —
// the layout only reads the resulting refs.
const { registrations, sleeves, isLoading: sidebarLoading } = useSidebarData(curHouseholdId)

// Inbox unread badge — module-scoped shared state across layouts.
// Background poll on the client every 5 minutes; pauses when the tab
// is hidden. Real freshness comes from user-action refreshes (inbox
// page mount, popover open, mark-read, etc.); the poll is just the
// safety net for "someone else sent me a message while I'm idling on
// another page." See composables/useInboxUnread.ts for the rationale.
const inboxUnread = useInboxUnread()
onMounted(() => {
  inboxUnread.refresh()
  inboxUnread.startPolling()
})
onBeforeUnmount(() => {
  inboxUnread.stopPolling()
})

// Portal-wide maintenance mode. Same poll cadence + lifecycle as the
// inbox unread badge. When the flag is on:
//   * Non-admins see a full-page friendly takeover (rendered below
//     instead of <slot />). Their data is unchanged; we just close
//     the front door so admins can work without concurrent client
//     traffic.
//   * Admins see a sticky amber banner above the normal layout so
//     they're reminded the portal is closed to everyone else.
const maintenance = useMaintenanceMode()
// Route the maintenance banner timestamp through the shared formatter so
// it honors the user's 12h/24h + date-style display prefs (was a raw
// `new Date(...).toLocaleString()`).
const { formatDateTime } = useFormatDate()
onMounted(() => {
  maintenance.refresh()
  maintenance.startPolling()
})
onBeforeUnmount(() => {
  maintenance.stopPolling()
})

const { me, hasRole } = useAuth()
// Real user identity + avatar URL. Reactive so the header updates the
// moment someone uploads a new picture via the profile page.
const { user: currentUser } = useCurrentUser()
// Tracks whether the avatar <img> failed to load (broken SAS, deleted
// blob, etc.) so the template can swap in the fallback icon. We can't
// simply hide the img on error any more because the loading skeleton
// occupies the same slot — without this flag, an errored img would
// leave a 36×36 blank circle next to the dropdown chevron.
const avatarErrored = ref(false)
// Reset the error flag whenever the avatar URL changes (e.g. after the
// user uploads a new picture and useCurrentUser.refresh() updates the
// cache-busted URL). Otherwise a one-time failure would mask later
// successful loads.
watch(() => currentUser.value?.avatarUrl, () => { avatarErrored.value = false })
// "View as" preview (admin/advisor previewing a lower role / a household).
const { canViewAs, realIsAdmin, active: viewAsActive, hasDisplayRole, startClient, startRole, startRep, exit: exitViewAs } = useViewAs()
// Admin-console visibility follows the DISPLAY role — hidden while previewing a
// non-admin role so the preview stays faithful.
const canViewAdminPage = computed(() => hasDisplayRole('Admin.Read') || hasDisplayRole('Admin.Write'))

// Deliberately SEPARATE from canViewAdminPage, which also grants the
// maintenance-mode bypass (the takeover screen below). Advisors get a narrow
// slice of the admin console but must still be locked out during maintenance
// like any other user, so widening canViewAdminPage would have quietly handed
// them a bypass. This flag only controls whether the nav entry is offered.
const canOpenAdminConsole = computed(() => canViewAdminPage.value || hasDisplayRole('Advisor'))
// While previewing AS a client, hide the household picker (a real client has
// none) so the preview matches what the client actually sees.
const isViewingAsClient = computed(() => viewAsActive.value && hasDisplayRole('Client'))

type MenuItemExt = DropdownMenuItem & {
  roles?: Role[]
  showIf?: boolean | ComputedRef<boolean> | (() => boolean)
}

// Multi-account "Switch user". Lists the accounts signed in on this device and
// switches between them with no re-login. Loaded on mount (below).
const { accounts: switchAccounts, activeAccount, load: loadAccounts, addAccount, switchTo, signOut: signOutAccount, signOutAll } = useAccountSwitcher()

// Global full-screen loader (account switch, etc.), rendered once below.
const { show: screenLoaderShow, label: screenLoaderLabel } = useScreenLoader()

function roleTag(a: { isAdmin: boolean, roles: string[] }): string {
  if (a.isAdmin) return 'Admin'
  if (a.roles.includes('Advisor')) return 'Advisor'
  if (a.roles.includes('Representative')) return 'Rep'
  return 'Client'
}

const profileItems = computed<MenuItemExt[][]>(() => {
  const accts = switchAccounts.value
  const active = activeAccount.value

  // "Switch user" submenu: one row per signed-in account (active checked, a
  // trailing ✕ to sign that account out — via the #switch-account-trailing
  // slot), then "Add another account" and (if >1) "Logout all".
  const accountChildren: (DropdownMenuItem & { sid?: string, active?: boolean })[] = accts.map(a => ({
    label: `${a.name || a.email || 'Account'} · ${roleTag(a)}`,
    icon: a.active ? 'i-lucide-check' : (a.isAdmin ? 'i-lucide-shield' : 'i-lucide-user'),
    slot: 'switch-account',
    sid: a.sid,
    active: a.active,
    onSelect: () => { if (!a.active) switchTo(a.sid) }
  }))
  accountChildren.push({
    label: 'Add another account',
    icon: 'i-lucide-user-plus',
    onSelect: () => addAccount()
  })
  if (accts.length > 1) {
    accountChildren.push({
      label: 'Logout all',
      icon: 'i-lucide-log-out',
      class: 'cursor-pointer',
      onSelect: async () => { await signOutAll() }
    })
  }

  // "View as" — only real admins/advisors, and only when not already previewing.
  // Admins can preview Client (the selected household) / Advisor / Representative;
  // advisors get Client only. While previewing, this becomes "Exit preview".
  const viewAsItems: MenuItemExt[] = []
  if (canViewAs.value && !viewAsActive.value) {
    const hhId = curHouseholdId.value
    const hhName = selectedHousehold.value?.label || tabSession.household?.name || null
    const children: DropdownMenuItem[] = [{
      label: hhId ? 'This household (Client)' : 'Pick a household first',
      icon: 'i-lucide-user',
      disabled: !hhId,
      onSelect: () => { if (hhId) startClient(hhId, hhName) }
    }]
    if (realIsAdmin.value) {
      // "A Representative" expands to the rep list (from the household picker's
      // rep source); picking one scopes the household list to that rep.
      const repChildren: DropdownMenuItem[] = (repItems.value || []).map((r: { repId: number, label: string }) => ({
        label: r.label,
        icon: 'i-lucide-user-round',
        onSelect: () => startRep(r.repId, r.label)
      }))
      children.push(
        { label: 'An Advisor', icon: 'i-lucide-briefcase', onSelect: () => startRole('Advisor') },
        repChildren.length
          ? { label: 'A Representative', icon: 'i-lucide-user-round', children: repChildren }
          : { label: 'A Representative (none available)', icon: 'i-lucide-user-round', disabled: true }
      )
    }
    viewAsItems.push({ label: 'VIEW AS', children })
  } else if (viewAsActive.value) {
    viewAsItems.push({ label: 'EXIT PREVIEW', class: 'cursor-pointer', onSelect: () => { exitViewAs() } })
  }

  const groups: MenuItemExt[][] = [
    [{
      label: 'Admin Console',
      to: '/admin/users',
      class: 'bg-[#3CDA7633] rounded-sm',
      // Visibility is gated by showIf (canOpenAdminConsole — admins plus
      // advisors, who land on the restricted two-tab console). The old `roles`
      // field was never read by filterMenuGroups, so it's dropped to keep types
      // clean.
      showIf: canOpenAdminConsole
    }, {
      // Above Profile because it is a destination people go to repeatedly,
      // where Profile/Settings are occasional. The unread count is appended to
      // the label rather than rendered as a separate badge slot — DropdownMenuItem
      // has no badge affordance, and the header bell already carries the visual
      // indicator; this is the same signal in a place you can click through.
      label: inboxUnread.unreadCount.value > 0
        ? `Inbox (${inboxUnread.unreadCount.value > 99 ? '99+' : inboxUnread.unreadCount.value})`
        : 'Inbox',
      to: '/inbox'
    }, {
      label: 'Profile',
      to: '/profile/user'
    }, {
      label: 'Settings',
      to: '/settings/display'
    }],
    ...(viewAsItems.length ? [viewAsItems] : []),
    [{
      label: 'SWITCH USER',
      children: accountChildren
    }],
    [{
      // Primary "Log out" signs out just the CURRENT account, handing off to
      // another signed-in account if one exists (else → /login).
      label: 'LOGOUT',
      class: 'cursor-pointer',
      onSelect: async () => { active ? await signOutAccount(active.sid) : await useLogout() }
    }]
  ]
  return groups
})

function resolveShowIf(showIf?: MenuItemExt['showIf']): boolean {
  if (showIf === undefined) return true
  if (typeof showIf === 'function') return showIf()
  if (isRef(showIf)) return showIf.value
  return showIf
}

function filterMenuGroups(items: MenuItemExt[][]): DropdownMenuItem[][] {
  return items
    .map(group => group.filter(item => resolveShowIf(item.showIf)))
    .filter(group => group.length > 0)
}

const filteredItems = computed(() => filterMenuGroups(profileItems.value))

// Profile menu open state, bound to the dropdown itself via v-model:open.
//
// This was a plain `let` flipped by an @click on the trigger, which had two
// problems. A non-ref isn't reactive, so the caret's :class never re-evaluated
// and the chevron never actually moved. And even made reactive, a hand-tracked
// flag desyncs: the menu also closes on outside-click, Escape, and picking an
// item, none of which fire the trigger's @click — so the caret would end up
// pointing the wrong way. Letting the component own the state fixes both.
const isProfileOpen = ref(false)

// ── Household value freshness (reliable client-side cache) ──────────────
// The selected household — including its market value shown in the header on
// almost every page — is persisted in tab storage for instant display. To keep
// that cache RELIABLE (not just time-based), we stamp it with the backend data
// version (the IQueryCache generation, surfaced on /api/me/last-import, which
// bumps on every import success + admin cache-bust) and refetch the value ONLY
// when the version actually changes — on page load and when the tab regains
// focus. Caching stays (no per-navigation fetch); staleness after an import or
// cache-bust is caught.

type HouseholdSummary = {
  householdId: number
  householdName: string
  repName: string | null
  currentValue: number | null
  isActive: boolean
} | null

async function fetchDataVersion(): Promise<number | null> {
  try {
    const r = await $fetch<{ generation?: number }>('/api/me/last-import', { credentials: 'include' })
    return typeof r?.generation === 'number' ? r.generation : null
  } catch { return null }
}

// Client: self-scoped household. Also catches an admin reassigning this user to
// a different household between sessions — hence the setHouseholdId cascade-
// clear. Always safe to call.
async function loadMyHousehold() {
  try {
    const h = await $fetch<HouseholdSummary>('/api/me/household', { credentials: 'include' })
    if (h) {
      tabSession.setHouseholdId(h.householdId)
      tabSession.setHouseholdObj({
        id: h.householdId,
        name: h.householdName,
        repName: h.repName ?? '',
        value: h.currentValue ?? 0
      })
    }
  } catch { /* keep the cached value on failure */ }
}

// Privileged: refresh the SELECTED household's value by id, then re-hydrate the
// picker's display from the updated store object.
async function loadSelectedHouseholdSummary() {
  const id = tabSession.householdId
  if (!id) return
  try {
    const h = await $fetch<HouseholdSummary>('/api/ui/household-summary', {
      query: { householdId: id }, credentials: 'include'
    })
    if (h) {
      tabSession.setHouseholdObj({
        id: h.householdId,
        name: h.householdName,
        repName: h.repName ?? '',
        value: h.currentValue ?? 0
      })
      restoreFromSession()
    }
  } catch { /* keep the cached value on failure */ }
}

async function refreshSelectedHouseholdValue() {
  if (hasRole('Client')) await loadMyHousehold()
  else await loadSelectedHouseholdSummary()
}

// Cheap version check → refetch the value only when the backend data moved.
// Throttled (10s) so rapid tab focus/blur toggling can't spam the endpoint —
// the backend rate-limits "read" too, this just avoids the wasted round-trips.
let lastRevalidateAt = 0
async function revalidateHouseholdVersion() {
  const now = Date.now()
  if (now - lastRevalidateAt < 10_000) return
  lastRevalidateAt = now
  const gen = await fetchDataVersion()
  if (tabSession.applyDataVersion(gen)) {
    await refreshSelectedHouseholdValue()
    // Re-resolve the footer "Market Value as of {date}"
    // (useMarketValueAsOfDate, keyed 'ui:market-asof'). It's import/override-
    // driven, and both a manual per-household refresh and a nightly bump the
    // generation — so when the version moves, the override (or cleared override)
    // takes effect here too, without a full reload.
    refreshNuxtData('ui:market-asof')
  }
}

onNuxtReady(async () => {
  // Restore the picker's selection from the persisted tab session.
  restoreFromSession()

  const gen = await fetchDataVersion()
  const versionChanged = tabSession.applyDataVersion(gen)

  if (hasRole('Client')) {
    // Always reload on initial mount — catches a between-session household
    // reassignment, not just a data-version change.
    await loadMyHousehold()
  } else if (versionChanged) {
    // Admin / Advisor / Rep: the persisted value is only stale if the backend
    // data version moved since we cached it.
    await loadSelectedHouseholdSummary()
  }
})

// Stale-while-revalidate: when the tab regains focus, cheaply re-check the data
// version and refetch the household value if it moved (e.g. a nightly import
// landed while the tab sat open).
function onFocusRevalidate() { void revalidateHouseholdVersion() }
function onVisibilityRevalidate() { if (!document.hidden) void revalidateHouseholdVersion() }
onMounted(() => {
  if (import.meta.client) {
    window.addEventListener('focus', onFocusRevalidate)
    document.addEventListener('visibilitychange', onVisibilityRevalidate)
  }
})
onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('focus', onFocusRevalidate)
    document.removeEventListener('visibilitychange', onVisibilityRevalidate)
  }
})
</script>

<template>
  <!-- Global full-screen loader — shown during whole-screen waits like an
       account switch (see useScreenLoader / useAccountSwitcher). -->
  <AppScreenLoader :show="screenLoaderShow" :label="screenLoaderLabel" />

  <!-- "Viewing as …" banner (admin/advisor preview). Only renders while a
       preview is active, so no layout impact in the normal case. -->
  <ViewAsBanner />

  <!-- Maintenance-mode non-admin takeover. When the portal-wide
       maintenance flag is on, everyone except Admin.Read/Admin.Write
       sees this full-page screen instead of the normal layout. The
       message text comes from whatever the admin typed in /admin/server
       so we can pass along context like "deploying release v4.2" or
       "DB upgrade in progress" without a code change. Their data is
       untouched on the backend — we just hide the front door so admins
       can work without concurrent client traffic. -->
    <div
      v-if="maintenance.state.value.enabled && !canViewAdminPage"
      class="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-tl from-[#C6C6BF] from-1% to-[#E0E0D2] p-6"
    >
      <div class="max-w-xl w-full bg-white rounded-2xl shadow-xl p-10 text-center">
        <div class="inline-flex items-center justify-center size-16 rounded-full bg-amber-100 mb-6">
          <UIcon name="i-lucide-wrench" class="size-8 text-amber-600" />
        </div>
        <h1 class="text-2xl font-bold text-[#455668] mb-3 uppercase tracking-wider">
          We'll be right back
        </h1>
        <p class="text-gray-700 text-base mb-4">
          The Azzad Asset Management portal is undergoing scheduled maintenance.
        </p>
        <div
          v-if="maintenance.state.value.message"
          class="bg-[#FAFAF2] border border-amber-200 rounded-lg p-4 mb-4 text-sm text-gray-800"
        >
          {{ maintenance.state.value.message }}
        </div>
        <p class="text-sm text-gray-500 mb-2">
          Your account and account data are safe and unaffected. Please check back in a few minutes.
        </p>
        <p
          v-if="maintenance.state.value.enabledAtUtc"
          class="text-xs text-gray-400 mt-6"
        >
          Maintenance started {{ formatDateTime(maintenance.state.value.enabledAtUtc) }}
        </p>
      </div>
    </div>

    <UDashboardGroup
      v-else
      unit="%"
      storage-key="azzad-dash-v2"
      class="text-black overflow-visible bg-elevated/25 bg-gradient-to-tl from-[#C6C6BF] from-1% to-[#E0E0D2] z-0"
    >
    <NavSidebar
      :selected-household="selectedHousehold"
      :registrations="registrations"
      :sleeves="sleeves"
      :loading="sidebarLoading"
      :household-name="selectedHousehold?.label ?? (tabSession.household ? tabSession.household.name : '') ?? ''"
      :household-rep-name="selectedHousehold?.repName ?? (tabSession.household ? tabSession.household.repName : '') ?? ''"
    />
    <UDashboardSearch :groups="groups" />

    <!-- `azzad-page-root` on the panel BODY carries `view-transition-name`
         (see the loading & motion block in assets/css/main.css). Tagging the
         element that hosts <slot /> — rather than the document root — is what
         keeps the sidebar, navbar and household header rock-steady through a
         navigation while only the page content cross-fades. It is a class on
         an element that already exists: no wrapper, no layout change. -->
    <UDashboardPanel
      id="main"
      class="overflow-hidden lg:rounded-l-3xl border-none bg-white"
      :divider="false"
      :ui="{ body: 'azzad-page-root p-0 sm:p-0 md:pl-[clamp(0.5rem,1.5vw,1.5rem)] [@media(orientation:landscape)_and_(max-height:600px)]:pl-6 flex-1 gap-0 min-h-[auto] overflow-x-clip min-w-0' }"
    >
      <template #header>
        <!-- The navbar lives in the panel HEADER (a flex sibling of the #body) so
             it does NOT inherit the body's `md:pl-[clamp(...)]` gutter — we mirror
             that exact clamp on the root, then push the toggle group right by pl-5
             (1.25rem = the title's ml-6 1.5rem minus the square button's p-1 0.25rem
             inner pad) so the ☰ GLYPH lands on the title's left edge. pl-5 on the
             `left` slot (not ml on the button) shifts BOTH the <lg auto-toggle and
             the lg+ collapse button. NB: never nest clamp() inside calc() in an
             arbitrary value — Tailwind silently drops the clamp; keep them split. -->
        <UDashboardNavbar title="" :ui="{ root: 'border-0 md:pl-[clamp(0.5rem,1.5vw,1.5rem)]', left: 'md:pl-5' }">
          <template #leading>
            <UDashboardSidebarCollapse
              class="cursor-pointer"
              icon="i-lucide-menu"
              size="lg"
              :color="('black' as any)"
            />
            <div v-if="canViewSelector && !isViewingAsClient" class="min-w-0">
              <UPopover
                arrow
                class="hover:bg-white cursor-pointer max-md:hidden md:max-w-[40vw]"
              >
                <UFieldGroup class="hover:bg-gray-100 rounded bg-[#FAFAF2] p-2 flex items-center justify-between gap-2 overflow-hidden">
                  <p class="text-black tracking-wider font-black text-[0.625rem] sm:text-xs truncate flex-1 min-w-0">
                    {{
                      selectedHousehold != null ? `${selectedHousehold.suffix} ${selectedHousehold.label}` : "Select a Household"
                    }}
                  </p>
                  <UIcon name="i-lucide-chevron-down" class="size-5 shrink-0" />
                </UFieldGroup>

                <template #content="{ close }">
                  <!-- Search palette + a filter funnel docked at the right of its
                       search bar. The funnel toggles an INLINE filter overlay (not
                       a nested popover — keeps this popover open + avoids focus /
                       z-index bugs). Filters are server-side + auto-apply (debounced
                       in the composable). Funnel turns green when a filter is on. -->
                  <div class="relative w-75 m-4">
                    <button
                      type="button"
                      class="absolute top-7 right-2.5 z-30 flex cursor-pointer items-center justify-center rounded p-1 hover:bg-gray-100"
                      :class="pickerFilterActive ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'"
                      aria-label="Filter households"
                      @click="showPickerFilters = !showPickerFilters"
                    >
                      <UIcon :name="showPickerFilters ? 'i-lucide-x' : 'i-lucide-filter'" class="size-4" />
                    </button>

                    <!-- Clear the typed search text. Docked just LEFT of the filter
                         funnel; only shown while there's text and the filter overlay
                         is closed (so we don't render two adjacent X icons). -->
                    <button
                      v-if="searchTerm && !showPickerFilters"
                      type="button"
                      class="absolute top-7 right-9 z-30 flex cursor-pointer items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      aria-label="Clear search"
                      @click="searchTerm = ''"
                    >
                      <UIcon name="i-lucide-x" class="size-4" />
                    </button>

                    <UCommandPalette
                      v-model="selectedHousehold"
                      v-model:search-term="searchTerm"
                      :loading="isSearching"
                      :groups="groups"
                      placeholder="Search for households..."
                      class="h-80 w-full"
                      :ui="{
                        input: `mt-4 ${searchTerm ? 'pr-16' : 'pr-9'} border-2 ${searchTerm ? 'border-green-700' : 'border-black/10'}`,
                        viewport: 'mt-4',
                        itemLabel: 'flex items-center min-w-0',
                        itemLabelBase: 'truncate min-w-0',
                        itemLabelSuffix: 'shrink-0'
                      }"
                      @update:model-value="(val) => { onSelect(val); close(); }"
                    />

                    <!-- Inline filter overlay — covers the results area when open. -->
                    <div
                      v-if="showPickerFilters"
                      class="absolute inset-x-0 top-14 bottom-0 z-20 flex flex-col gap-3 overflow-y-auto bg-white px-1 pt-2"
                    >
                      <div class="flex flex-col gap-1">
                        <label class="text-[0.625rem] font-bold uppercase tracking-widest text-[#A5B0BB]">
                          Sort by
                        </label>
                        <AppSelect v-model="sortBy" :items="sortOptions" class="w-full" title="Sort by" />
                      </div>
                      <div class="flex flex-col gap-1">
                        <label class="text-[0.625rem] font-bold uppercase tracking-widest text-[#A5B0BB]">
                          Household AUM ($)
                        </label>
                        <div class="flex items-center gap-2">
                          <UInput v-model="minValue" type="number" placeholder="Min" class="w-full" />
                          <span class="text-gray-400">–</span>
                          <UInput v-model="maxValue" type="number" placeholder="Max" class="w-full" />
                        </div>
                      </div>
                      <div class="flex flex-col gap-1">
                        <label class="text-[0.625rem] font-bold uppercase tracking-widest text-[#A5B0BB]">
                          Inception (start date)
                        </label>
                        <div class="flex items-center gap-2">
                          <UInput v-model="inceptionFrom" type="date" class="w-full" />
                          <span class="text-gray-400">–</span>
                          <UInput v-model="inceptionTo" type="date" class="w-full" />
                        </div>
                      </div>

                      <!-- Advisor (representative) filter — Admin + Advisor only. -->
                      <div v-if="canFilterByRep" class="flex flex-col gap-1">
                        <label class="text-[0.625rem] font-bold uppercase tracking-widest text-[#A5B0BB]">
                          Advisor
                        </label>
                        <div
                          v-if="repId != null"
                          class="flex items-center justify-between gap-2 rounded border border-black/10 bg-[#F6F7F7] px-2 py-1"
                        >
                          <span class="text-xs font-medium text-black truncate">{{ selectedRepName }}</span>
                          <UButton size="xs" color="neutral" variant="ghost" icon="i-lucide-x" aria-label="Clear advisor" @click="clearRep" />
                        </div>
                        <template v-else>
                          <UInput
                            v-model="repSearch"
                            placeholder="Search advisor…"
                            size="sm"
                            :loading="repIsSearching"
                            icon="i-lucide-search"
                          />
                          <div class="max-h-40 overflow-y-auto rounded border border-black/10 divide-y divide-black/5">
                            <button
                              v-for="rep in repItems"
                              :key="rep.repId"
                              type="button"
                              class="block w-full cursor-pointer px-2 py-1.5 text-left hover:bg-[#F6F7F7]"
                              @click="pickRep(rep)"
                            >
                              <div class="text-xs font-medium text-black truncate">{{ rep.label }}</div>
                              <div v-if="rep.sub" class="text-[0.625rem] text-gray-500 truncate">{{ rep.sub }}</div>
                            </button>
                            <div v-if="!repItems.length" class="px-2 py-2 text-xs text-gray-400">
                              {{ repIsSearching ? 'Searching…' : 'No advisors' }}
                            </div>
                          </div>
                        </template>
                      </div>

                      <div class="flex justify-end pt-1">
                        <UButton
                          size="sm"
                          color="neutral"
                          variant="soft"
                          icon="i-lucide-rotate-ccw"
                          :disabled="!pickerFilterActive"
                          @click="resetPickerFilters"
                        >
                          Reset
                        </UButton>
                      </div>
                    </div>
                  </div>
                </template>
              </UPopover>

              <!-- Mobile (<md): the popover above is hidden; this pill opens a
                   full-screen bottom sheet with the same search palette. -->
              <button
                type="button"
                class="md:hidden w-full max-w-[60vw] hover:bg-gray-100 rounded bg-[#FAFAF2] p-2 flex items-center justify-between gap-2 overflow-hidden cursor-pointer"
                @click="mobilePickerOpen = true"
              >
                <span class="text-black tracking-wider font-black text-[0.625rem] truncate flex-1 min-w-0 text-left">
                  {{ selectedHousehold != null ? `${selectedHousehold.suffix} ${selectedHousehold.label}` : "Select a Household" }}
                </span>
                <UIcon name="i-lucide-chevron-down" class="size-5 shrink-0" />
              </button>

              <USlideover
                v-model:open="mobilePickerOpen"
                side="bottom"
                title="Select a household"
                :ui="{ content: 'h-[88vh] rounded-t-2xl' }"
              >
                <template #body>
                  <!-- Same search + funnel + filter overlay as the desktop popover,
                       sharing the same composable refs (so the X clear + all filters
                       work identically on mobile). -->
                  <div class="relative w-full h-full">
                    <button
                      type="button"
                      class="absolute top-2.5 right-2.5 z-30 flex cursor-pointer items-center justify-center rounded p-1 hover:bg-gray-100"
                      :class="pickerFilterActive ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'"
                      aria-label="Filter households"
                      @click="showPickerFilters = !showPickerFilters"
                    >
                      <UIcon :name="showPickerFilters ? 'i-lucide-x' : 'i-lucide-filter'" class="size-5" />
                    </button>

                    <!-- Clear the typed search text. -->
                    <button
                      v-if="searchTerm && !showPickerFilters"
                      type="button"
                      class="absolute top-2.5 right-11 z-30 flex cursor-pointer items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      aria-label="Clear search"
                      @click="searchTerm = ''"
                    >
                      <UIcon name="i-lucide-x" class="size-5" />
                    </button>

                    <UCommandPalette
                      v-model="selectedHousehold"
                      v-model:search-term="searchTerm"
                      :loading="isSearching"
                      :groups="groups"
                      placeholder="Search for households..."
                      class="h-full w-full"
                      :ui="{
                        input: `${searchTerm ? 'pr-20' : 'pr-12'} border-2 ${searchTerm ? 'border-green-700' : 'border-black/10'}`,
                        itemLabel: 'flex items-center min-w-0',
                        itemLabelBase: 'truncate min-w-0',
                        itemLabelSuffix: 'shrink-0'
                      }"
                      @update:model-value="(val) => { onSelect(val); mobilePickerOpen = false }"
                    />

                    <!-- Inline filter overlay — covers the results area when open. -->
                    <div
                      v-if="showPickerFilters"
                      class="absolute inset-x-0 top-12 bottom-0 z-20 flex flex-col gap-3 overflow-y-auto bg-white px-1 pt-2"
                    >
                      <div class="flex flex-col gap-1">
                        <label class="text-[0.625rem] font-bold uppercase tracking-widest text-[#A5B0BB]">
                          Sort by
                        </label>
                        <AppSelect v-model="sortBy" :items="sortOptions" class="w-full" title="Sort by" />
                      </div>
                      <div class="flex flex-col gap-1">
                        <label class="text-[0.625rem] font-bold uppercase tracking-widest text-[#A5B0BB]">
                          Household AUM ($)
                        </label>
                        <div class="flex items-center gap-2">
                          <UInput v-model="minValue" type="number" placeholder="Min" class="w-full" />
                          <span class="text-gray-400">–</span>
                          <UInput v-model="maxValue" type="number" placeholder="Max" class="w-full" />
                        </div>
                      </div>
                      <div class="flex flex-col gap-1">
                        <label class="text-[0.625rem] font-bold uppercase tracking-widest text-[#A5B0BB]">
                          Inception (start date)
                        </label>
                        <div class="flex items-center gap-2">
                          <UInput v-model="inceptionFrom" type="date" class="w-full" />
                          <span class="text-gray-400">–</span>
                          <UInput v-model="inceptionTo" type="date" class="w-full" />
                        </div>
                      </div>

                      <div v-if="canFilterByRep" class="flex flex-col gap-1">
                        <label class="text-[0.625rem] font-bold uppercase tracking-widest text-[#A5B0BB]">
                          Advisor
                        </label>
                        <div
                          v-if="repId != null"
                          class="flex items-center justify-between gap-2 rounded border border-black/10 bg-[#F6F7F7] px-2 py-1"
                        >
                          <span class="text-xs font-medium text-black truncate">{{ selectedRepName }}</span>
                          <UButton size="xs" color="neutral" variant="ghost" icon="i-lucide-x" aria-label="Clear advisor" @click="clearRep" />
                        </div>
                        <template v-else>
                          <UInput
                            v-model="repSearch"
                            placeholder="Search advisor…"
                            size="sm"
                            :loading="repIsSearching"
                            icon="i-lucide-search"
                          />
                          <div class="max-h-40 overflow-y-auto rounded border border-black/10 divide-y divide-black/5">
                            <button
                              v-for="rep in repItems"
                              :key="rep.repId"
                              type="button"
                              class="block w-full cursor-pointer px-2 py-1.5 text-left hover:bg-[#F6F7F7]"
                              @click="pickRep(rep)"
                            >
                              <div class="text-xs font-medium text-black truncate">{{ rep.label }}</div>
                              <div v-if="rep.sub" class="text-[0.625rem] text-gray-500 truncate">{{ rep.sub }}</div>
                            </button>
                            <div v-if="!repItems.length" class="px-2 py-2 text-xs text-gray-400">
                              {{ repIsSearching ? 'Searching…' : 'No advisors' }}
                            </div>
                          </div>
                        </template>
                      </div>

                      <div class="flex justify-end pt-1">
                        <UButton
                          size="sm"
                          color="neutral"
                          variant="soft"
                          icon="i-lucide-rotate-ccw"
                          :disabled="!pickerFilterActive"
                          @click="resetPickerFilters"
                        >
                          Reset
                        </UButton>
                      </div>
                    </div>
                  </div>
                </template>
              </USlideover>
            </div>
          </template>
          <template #right>
            <!-- Single combined Inbox + Notifications button. Opens a
                 popover with two tabs. Reduces header crowding (the two
                 separate icons were clipping into the household
                 selector) and gives users one consistent surface for
                 "anything new?" on both desktop and mobile. -->
            <div class="mr-3">
              <InboxNotificationsPopover />
            </div>
            <UDropdownMenu
              v-if="me.authenticated"
              v-model:open="isProfileOpen"
              :items="filteredItems"
              class="hover:bg-white mr-3"
              :ui="{
                content: 'w-64 bg-white px-2 py-3 shadow-lg -100 mt-2 text-black text-sm font-bold uppercase tracking-widest'
              }"
            >
              <UFieldGroup
                class="cursor-pointer hover:bg-gray-100 rounded items-center gap-3 p-1 select-none hover:transition-transform duration-300"
              >
                <!-- While /api/auth/me + the avatar SAS lookup are in
                     flight, currentUser is null. Show a skeleton block
                     for the name and a circular skeleton for the avatar
                     so the header doesn't render a half-empty pill. -->
                <AppSkeleton
                  v-if="!currentUser"
                  width="w-32"
                  height="h-4"
                  class="hidden sm:block"
                />
                <p
                  v-else
                  class="uppercase font-semibold trailing-wider text-[#455668] text-sm hidden sm:block"
                >
                  {{ currentUser?.name || me.claims?.name || me.claims?.preferred_username }}
                </p>
                <!-- Plain <img> rather than UAvatar so the URL bypasses
                     @nuxt/image's IPX optimizer. IPX rejects /api/* paths
                     as "FORBIDDEN_PATH" because they aren't under public/.
                     We don't need optimization here anyway — the avatar is
                     a small SAS-redirected image.

                     Three render states:
                       1. Loading (currentUser null)  → USkeleton circle
                       2. Loaded with avatarUrl       → <img> from BFF
                       3. <img> errored at runtime    → fallback user icon
                          (toggled by avatarErrored ref so the broken
                           image alt-text doesn't show through). -->
                <AppSkeleton
                  v-if="!currentUser"
                  circle
                  width="size-9"
                  height="size-9"
                />
                <img
                  v-else-if="currentUser.avatarUrl && !avatarErrored"
                  :src="currentUser.avatarUrl"
                  :alt="currentUser?.name || 'User'"
                  class="size-9 rounded-full object-cover bg-gray-100"
                  @error="avatarErrored = true"
                >
                <span
                  v-else
                  class="size-9 rounded-full bg-gray-100 inline-flex items-center justify-center text-gray-400"
                  :title="currentUser?.name || 'User'"
                >
                  <UIcon name="i-lucide-user-round" class="size-5" />
                </span>

                <!-- chevron-DOWN at rest, rotating 180° to point up when open —
                     matching the household picker's caret above and the usual
                     "down means there's more below" convention.
                     rotate-180 (2D), not rotate-x-180: the latter is a 3D X-axis
                     rotation that needs a transform-3d/perspective context, so on
                     a flat icon it reads as a vertical flip rather than a turn. -->
                <UIcon
                  name="i-lucide-chevron-down"
                  class="size-5 shrink-0 transition-transform duration-300 ease-in-out"
                  :class="{ 'rotate-180': isProfileOpen }"
                />
              </UFieldGroup>

              <!-- Per-account ✕ to sign that account out, on each Switch-user
                   row. The active account has none (use Logout).

                   EVERY ONE of these .stop modifiers is load-bearing, because the
                   row selects itself through THREE separate paths and `click.stop`
                   alone stops none of them. Reka UI's MenuItem does:

                     onPointerdown: () => { isPointerDownRef = true }
                     onPointerup:   (e) => { if (!isPointerDownRef) e.currentTarget.click() }
                     onKeydown:     (e) => { if (SELECTION_KEYS) e.currentTarget.click() }
                     onClick:       handleSelect

                   `currentTarget` there is the ROW, so the row dispatches a click on
                   ITSELF — it never bubbles up from this button, and a click handler
                   here cannot cancel it. Worse, stopping pointerdown (which we must,
                   to keep the row from starting a selection) leaves isPointerDownRef
                   false, which makes the pointerup branch fire EVERY time. That is
                   why the ✕ reliably switched accounts instead of removing one.

                   So: stop pointerup as well, and stop Enter/Space so keyboard users
                   aren't switched either. Only the selection keys are stopped —
                   swallowing all keydown would break arrow-key menu navigation.
                   stopPropagation doesn't suppress this button's own activation, so
                   @click below still runs for both mouse and keyboard. -->
              <template #switch-account-trailing="{ item }">
                <button
                  v-if="!(item as any).active"
                  type="button"
                  class="shrink-0 -my-1 ml-1 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                  :aria-label="`Sign out ${(item as any).label}`"
                  @pointerdown.stop
                  @pointerup.stop
                  @keydown.enter.stop
                  @keydown.space.stop
                  @click.stop.prevent="signOutAccount((item as any).sid)"
                >
                  <UIcon name="i-lucide-x" class="size-4" />
                </button>
              </template>
            </UDropdownMenu>
          </template>
        </UDashboardNavbar>
      </template>
      <template #body>
        <!-- Admin-only sticky banner when maintenance is on. Non-admins
             see the full-page takeover above instead; this banner is
             the admin's reminder that the portal is closed to everyone
             else while they work. Clicking it jumps to /admin/server
             where the flag can be turned off again. -->
        <NuxtLink
          v-if="maintenance.state.value.enabled && canViewAdminPage"
          to="/admin/server"
          class="block sticky top-0 z-40 bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-amber-200 transition-colors"
        >
          <UIcon name="i-lucide-wrench" class="size-4 shrink-0" />
          <span class="flex-1 truncate">
            Maintenance mode is ON — non-admins see the takeover screen.
            <span v-if="maintenance.state.value.message" class="font-normal opacity-80">
              ({{ maintenance.state.value.message }})
            </span>
          </span>
          <span class="text-xs underline shrink-0">Manage</span>
        </NuxtLink>
        <slot />
      </template>
      <template #footer>
        <FooterMainInfo />
      </template>
    </UDashboardPanel>
    </UDashboardGroup>
</template>

<style scoped>
::v-deep(.slot-wrapper) {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
</style>
